"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParametersValidator = void 0;
const class_validator_1 = require("class-validator");
const ajv_1 = __importDefault(require("ajv"));
const fluent_schema_1 = __importDefault(require("fluent-schema"));
const shared_dto_1 = require("../dto/shared.dto");
const SdkError_1 = __importDefault(require("./SdkError"));
const did = 'did';
const didMethod = 'didMethod';
const jwt = 'jwt';
const array = 'array';
const number = 'number';
const object = 'object';
const string = 'string';
const boolean = 'boolean';
const isoString = 'isoString';
const confirmationCode = 'confirmationCode';
const password = 'password';
const primitives = [did, didMethod, jwt, array, number, object, string, boolean, isoString, confirmationCode, password];
const jsonSchemas = {
    VCV1: fluent_schema_1.default.object()
        .prop('@context', fluent_schema_1.default.anyOf([fluent_schema_1.default.array().items(fluent_schema_1.default.anyOf([fluent_schema_1.default.string(), fluent_schema_1.default.object()])), fluent_schema_1.default.object(), fluent_schema_1.default.string()]))
        .prop('id', fluent_schema_1.default.string())
        .prop('type', fluent_schema_1.default.array().items(fluent_schema_1.default.string()))
        .prop('holder', fluent_schema_1.default.object().prop('id', fluent_schema_1.default.string()).required(['id']))
        // Credential Subject may be a single item or an array of items
        .prop('credentialSubject', fluent_schema_1.default.anyOf([fluent_schema_1.default.array(), fluent_schema_1.default.object()]))
        .prop('issuanceDate', fluent_schema_1.default.string().format('date-time'))
        .prop('expirationDate', fluent_schema_1.default.string().format('date-time'))
        .prop('revocation', fluent_schema_1.default.object().prop('id', fluent_schema_1.default.string()).required(['id']))
        .required(['@context', 'id', 'type', 'holder', 'credentialSubject', 'issuanceDate']),
};
const isPrimitive = (schema) => primitives.includes(schema);
class ParametersValidator {
    static validate(schemas) {
        return __awaiter(this, void 0, void 0, function* () {
            const allErrors = [];
            for (const [index, schema] of schemas.entries()) {
                const { isArray, type, isRequired, value: SchemaValue } = schema;
                let errors = [];
                const isArrayValid = Array.isArray(SchemaValue) && SchemaValue;
                if (isArray && isArrayValid && SchemaValue.length === 0) {
                    continue;
                }
                if (isArray && !isArrayValid) {
                    const message = `Parameter at index [${index}] should be an array.`;
                    const error = { value: SchemaValue, message };
                    allErrors.push(error);
                    continue;
                }
                if (isArray) {
                    const items = SchemaValue;
                    for (const item of items) {
                        errors = yield ParametersValidator.process({ type, isRequired, value: item }, index);
                        allErrors.push(...errors);
                    }
                }
                else {
                    errors = yield ParametersValidator.process(schema, index);
                    allErrors.push(...errors);
                }
            }
            if (allErrors.length > 0) {
                throw new SdkError_1.default('COR-1', { errors: allErrors });
            }
        });
    }
    static validatePrimitive(schema, value) {
        let message;
        let isValid = true;
        switch (schema) {
            case did:
                isValid = class_validator_1.matches(value, shared_dto_1.DID);
                message = `Parameter "${value}" is not a valid. Valid format: (${shared_dto_1.DID}).`;
                break;
            case didMethod:
                isValid = class_validator_1.matches(value, shared_dto_1.DID_METHOD);
                message = `Parameter "${value}" is not a valid. Valid format: (${shared_dto_1.DID_METHOD}).`;
                break;
            case jwt:
                isValid = class_validator_1.matches(value, shared_dto_1.JWT);
                message = `Parameter "${value}" is not a valid JWT. Valid format: (${shared_dto_1.JWT}).`;
                break;
            case array:
                isValid = class_validator_1.isArray(value);
                message = `Parameter "${value}" should be an array.`;
                break;
            case number:
                isValid = class_validator_1.isNumber(value);
                message = `Parameter "${value}" should be a number.`;
                break;
            case object:
                isValid = class_validator_1.isObject(value);
                message = `Parameter "${value}" should be an object.`;
                break;
            case string:
                isValid = class_validator_1.isString(value);
                message = `Parameter "${value}" should be a string.`;
                break;
            case boolean:
                isValid = class_validator_1.isBoolean(value);
                message = `Parameter "${value}" should be a boolean.`;
                break;
            case isoString:
                isValid = class_validator_1.isISO8601(value);
                message = `Parameter "${value}" is not a valid ISO 8601 date string.`;
                break;
            case confirmationCode:
                isValid = class_validator_1.matches(value, shared_dto_1.COGNITO_CONFIRMATION_CODE);
                message = `Parameter "${value}" is not a valid confirmation code. Valid format: (${shared_dto_1.COGNITO_CONFIRMATION_CODE}).`;
                break;
            case password:
                isValid = class_validator_1.matches(value, shared_dto_1.PASSWORD);
                message = `Parameter "${value}" is not a password. Valid format: (${shared_dto_1.PASSWORD}).`;
                break;
        }
        return { isValid, message };
    }
    static process(schema, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const { type: Schema, isRequired, value: SchemaValue } = schema;
            const allErrors = [];
            const isUndefined = !class_validator_1.isDefined(SchemaValue);
            if (isRequired && isUndefined) {
                allErrors.push({ value: SchemaValue, message: `Required parameter at index [${index}] is missing.` });
            }
            if (isUndefined) {
                return allErrors;
            }
            if (isPrimitive(Schema)) {
                const { isValid, message } = ParametersValidator.validatePrimitive(Schema, SchemaValue);
                if (!isValid) {
                    allErrors.push({ value: SchemaValue, message });
                }
            }
            else if (Object.keys(jsonSchemas).indexOf(Schema) !== -1) {
                try {
                    const ajv = new ajv_1.default();
                    const isValid = ajv.validate(jsonSchemas[Schema].valueOf(), SchemaValue);
                    if (!isValid) {
                        allErrors.push({ value: SchemaValue, message: ajv.errorsText() });
                    }
                }
                catch (error) {
                    allErrors.push({ value: SchemaValue, message: error });
                }
            }
            else {
                const schema = new Schema();
                for (const [key, value] of Object.entries(SchemaValue)) {
                    schema[key] = value;
                }
                const errors = yield class_validator_1.validate(schema);
                const schemaErrors = [];
                for (const error of errors) {
                    const { property, value, constraints } = error;
                    schemaErrors.push({
                        argument: property,
                        value: value,
                        message: constraints,
                    });
                }
                if (schemaErrors.length > 0) {
                    allErrors.push(schemaErrors);
                }
            }
            return allErrors;
        });
    }
}
exports.ParametersValidator = ParametersValidator;
//# sourceMappingURL=ParametersValidator.js.map